import { useState } from 'react';
import { ApiError } from '../../lib/api';
import type { GridFilters } from '../../lib/gridFilterUrl';
import type { GoogleValidationResult } from '../../lib/validateGoogleContent';
import { GoogleAdsExportModal } from './GoogleAdsExportModal';
import { GoogleBudgetExportModal } from './GoogleBudgetExportModal';
import { useGoogleExport } from './useGoogleExport';

// Two buttons rendered in the Google grid header (spec §2.1) — Export
// Ads CSV and Export Budgets CSV. Each opens the matching modal; the
// hook handles auth + filename + blob download.
//
// Disabled rules per spec §2.1:
//   - while the grid query is loading
//   - when the rollout is archived (no exports from archived rollouts)

type Props = {
  rolloutId: string;
  filters: GridFilters;
  filteredCount: number;
  gateCount: number;
  archived: boolean;
  loading: boolean;
  // Pre-export validation results, one per scope. Pre-computed by the
  // grid so the export modals can flip into a "View Errors / Export
  // Anyway" gate when the user-selected scope has any violations.
  // Optional during the staged rollout — when omitted, the gate is
  // skipped and modals render as before.
  validationFiltered?: GoogleValidationResult;
  validationGate?: GoogleValidationResult;
  // Hand-off to the grid's errors modal when the user clicks "View
  // Errors" inside an export modal. Carries the export scope so the
  // errors modal opens with the same row set the export was about
  // to operate on.
  onShowErrors?: (scope: 'filtered' | 'all') => void;
};

type OpenModal = null | 'ads' | 'budget';

export function GoogleExportButtons({
  rolloutId,
  filters,
  filteredCount,
  gateCount,
  archived,
  loading,
  validationFiltered,
  validationGate,
  onShowErrors,
}: Props) {
  const [open, setOpen] = useState<OpenModal>(null);
  const [error, setError] = useState<string | null>(null);
  const { busy, exportAdsCsv, exportBudgetCsv } = useGoogleExport();

  const disabled = loading || archived;
  const tooltip = archived
    ? 'Archived rollouts cannot be exported.'
    : loading
      ? 'Loading…'
      : undefined;

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setError(null);
            setOpen('ads');
          }}
          title={tooltip}
          className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Export Ads CSV
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setError(null);
            setOpen('budget');
          }}
          title={tooltip}
          className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Export Budgets CSV
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-600 mt-1" role="alert">
          {error}
        </p>
      )}

      {open === 'ads' && (
        <GoogleAdsExportModal
          filteredCount={filteredCount}
          gateCount={gateCount}
          busy={busy}
          filteredViolationCount={validationFiltered?.violations.length ?? 0}
          filteredLocationCount={validationFiltered?.locationCount ?? 0}
          gateViolationCount={validationGate?.violations.length ?? 0}
          gateLocationCount={validationGate?.locationCount ?? 0}
          onCancel={() => setOpen(null)}
          onShowErrors={
            onShowErrors === undefined
              ? undefined
              : (scope) => {
                  // Hand off to the grid's errors modal at the same
                  // scope. Close the export modal so the user lands
                  // on the errors modal cleanly.
                  setOpen(null);
                  onShowErrors(scope);
                }
          }
          onGenerate={async ({ scope, campaignNameTemplate, adGroup }) => {
            try {
              await exportAdsCsv({
                rolloutId,
                scope,
                filters,
                campaignNameTemplate,
                adGroup,
              });
              setOpen(null);
            } catch (err) {
              setError(err instanceof ApiError ? err.message : String(err));
            }
          }}
        />
      )}

      {open === 'budget' && (
        <GoogleBudgetExportModal
          filteredCount={filteredCount}
          gateCount={gateCount}
          busy={busy}
          filteredViolationCount={validationFiltered?.violations.length ?? 0}
          filteredLocationCount={validationFiltered?.locationCount ?? 0}
          gateViolationCount={validationGate?.violations.length ?? 0}
          gateLocationCount={validationGate?.locationCount ?? 0}
          onCancel={() => setOpen(null)}
          onShowErrors={
            onShowErrors === undefined
              ? undefined
              : (scope) => {
                  setOpen(null);
                  onShowErrors(scope);
                }
          }
          onGenerate={async ({ scope, campaignNameTemplate }) => {
            try {
              await exportBudgetCsv({
                rolloutId,
                scope,
                filters,
                campaignNameTemplate,
              });
              setOpen(null);
            } catch (err) {
              setError(err instanceof ApiError ? err.message : String(err));
            }
          }}
        />
      )}
    </>
  );
}
